import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  LockProvider,
  templateToObjecType,
} from '@work4all/components/lib/hooks';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { CommentProvider } from '@work4all/utils';

import { resolveEntityVariant } from '../../file-entities-lists';
import { ExtendedEntities } from '../../file-entities-lists/ExtendedEntities.enum';

import { useAfterSave } from './hooks/use-after-save';
import { useMaskMetaData } from './hooks/use-mask-meta-data';
import { OpenMaskOverride, useOpenMaskOverlay } from './hooks/use-open-mask';
import { NEW_ENTITY_ID } from './mask-metadata';
import { RestOverlay } from './RestOverlay';
import { IRouteParams, MaskTemplateEntity } from './types';
import { getMaskControllerComponent } from './utils/get-mask-controller-component';

export const MaskOverlay: React.FC<{ amplitudeEntryPoint: string }> = (
  props
) => {
  const metaData = useMaskMetaData();
  const [searchParams] = useSearchParams();
  const maskOpenHandler = useOpenMaskOverlay();

  const handleAfterSave = useAfterSave(props?.amplitudeEntryPoint);
  const handleAfterSaveWithoutClose = useAfterSave(
    props?.amplitudeEntryPoint,
    true
  );

  useEffect(() => {
    if (!metaData) return;

    const id =
      metaData.subEntityId === NEW_ENTITY_ID ? null : metaData.subEntityId;
    sendAmplitudeData(
      id === null || id === NEW_ENTITY_ID
        ? EventType.AddElement
        : EventType.EditElement,
      {
        name: metaData.subEntityType,
        entryPoint:
          props.amplitudeEntryPoint === 'fileDetailPage'
            ? metaData.entityType
            : props.amplitudeEntryPoint,
      }
    );
  }, [metaData, props.amplitudeEntryPoint]);

  if (!metaData) {
    console.warn('MaskOverlay: metaData is null, cannot render mask overlay');
    return null;
  }

  const resolved = resolveEntityVariant(metaData.subEntityType);
  const entity = resolved.entityType;
  const entityVariant = resolved.entityVariant;

  const openMaskWithNewId = (newId: string | number) => {
    maskOpenHandler().handler({
      entity: entity as Entities,
      entityVariant,
      id: newId,
    });
  };

  const handleAfterQuickSave = (result: { id?: string | number } | null) => {
    // If we received a new entity ID after quick save
    if (result && result.id) {
      openMaskWithNewId(result.id);
    } else {
      handleAfterSaveWithoutClose(); // For quick save when in 'edit' mode
    }
  };

  const id =
    metaData.subEntityId === NEW_ENTITY_ID ? null : metaData.subEntityId;
  const template =
    tryParseTemplateFromSearchParams(searchParams) ??
    tryParseTemplateFromRouteParams(metaData);
  const params = parseCustomParamsFromSearchParams(searchParams);
  const openTab = searchParams.get('openTab') ?? undefined;

  const MaskController = getMaskControllerComponent(entity);

  if (MaskController === RestOverlay) {
    return <RestOverlay />;
  }

  // If MaskController is not RestOverlay, it is a valid entity
  assertEntityType(entity);

  return (
    <LockProvider
      autoLock={!!id}
      entity={entity}
      entityIds={id}
      forcedObjectType={templateToObjecType(entity, template)}
    >
      <OpenMaskOverride value={{ onOpenMask: maskOpenHandler }}>
        <CommentProvider>
          <MaskController
            key={`${entity}_${id}`}
            entity={entity}
            entityVariant={entityVariant}
            id={id}
            template={template}
            openTab={openTab}
            params={params}
            onAfterSave={handleAfterSave}
            onAfterQuickSave={handleAfterQuickSave}
            amplitudeEntryPoint={props.amplitudeEntryPoint}
            data-test-id="maskForm"
          />
        </CommentProvider>
      </OpenMaskOverride>
    </LockProvider>
  );
};

function tryParseTemplateFromSearchParams(
  searchParams: URLSearchParams
): MaskTemplateEntity | null {
  const raw = searchParams.get('template');

  if (!raw) {
    return null;
  }

  const result = /^(?<entity>.+?):(?<ids>.+)$/.exec(raw);

  if (!result) {
    return null;
  }

  const { entity, ids } = result.groups;

  const normalizedIds = ids.split(',').map((id) => Number(id) || id);

  return { entity: entity as Entities, ids: normalizedIds };
}

function tryParseTemplateFromRouteParams(
  params: IRouteParams
): MaskTemplateEntity | null {
  const entity = pathnameToEntity(params.entityType);
  const ids = params.entityId;

  if (!entity || !ids) {
    return null;
  }

  const normalizedIds = ids.split(',').map((id) => Number(id) || id);

  return { entity, ids: normalizedIds };
}

function pathnameToEntity(pathname: string): Entities | null {
  switch (pathname) {
    case 'customers':
      return Entities.customer;
    case 'suppliers':
      return Entities.supplier;
    case 'projects':
      return Entities.project;

    default:
      console.warn(`Could not parse entity from pathname: ${pathname}`);
      return null;
  }
}

function parseCustomParamsFromSearchParams(searchParams: URLSearchParams): {
  [key: string]: string;
} {
  const result: { [key: string]: string } = {};

  searchParams.forEach((value, key) => {
    // Template is a special parameter and will be passed separately from other
    // params after processing.
    if (!['template', 'openTab'].includes(key)) {
      result[key] = value;
    }
  });

  return result;
}

function assertEntityType(
  entity: ExtendedEntities
): asserts entity is Entities {
  // Does nothing, but TypeScript will use the correct type after this function.
  // Should only be called with entities that are known to be valid.
}
